import {
  DataEntryOption,
  DetailRequirement,
  EngagementTaskTrait,
  ResourceDataType,
} from '@cibo/core'
import { Display } from '../../Display'
import { DataEntryCheckboxList } from './DataEntryCheckboxList'
import { DataEntryFileUpload } from './DataEntryFileUpload'
import { DataEntryGateGroup } from './DataEntryGateGroup'
import { DataEntryRadioGroup } from './DataEntryRadioGroup'
import { DataEntryTilesSelector } from './DataEntryTilesSelector'
import { DatePickerTraitEditor } from './DatePickerTraitEditor'
import { EmailDataEntryField } from './EmailDataEntryField'
import { LongTextDataEntryField } from './LongTextDataEntryField'
import { NumberDataEntryField } from './NumberDataEntryField'
import { SingleTargetSchemaEditor } from './SingleTargetSchemaEditor'

type SingleTargetEditorUiProps = {
  traitStatus: EngagementTaskTrait
  detailRequirement?: DetailRequirement
  traitDetailValue: any
  handleSaveTraitDetail: (detail: { value: any; year?: number }) => void
  traitDetail: any
  taskData: any
  resolvedDataType: ResourceDataType
  taskId: string
  resourceId: string
  saveError: Error | undefined
  ciboSourced: boolean
}

export const SingleTargetEditorUi = (props: SingleTargetEditorUiProps) => {
  const {
    traitStatus,
    detailRequirement,
    traitDetailValue,
    handleSaveTraitDetail,
    traitDetail,
    taskData,
    taskId,
    resourceId,
    saveError,
    ciboSourced,
    resolvedDataType,
  } = props

  const params = traitStatus?.params

  if (traitStatus.schema) {
    return (
      <SingleTargetSchemaEditor
        traitStatus={traitStatus}
        handleSaveTraitDetail={handleSaveTraitDetail}
        traitDetailValue={traitDetailValue}
        existingDetailSource={traitDetail?.source}
        immutable={traitDetail?.immutable}
        year={detailRequirement?.year}
        renderEmbeddedDisplay={field => (
          <Display display={field} task={null as any} TaskEditorComponent={() => <></>} />
        )}
      />
    )
  }

  switch (traitStatus.displayType) {
    case 'boolean':
      return (
        <DataEntryRadioGroup
          traitConfig={traitStatus}
          detailRequirement={detailRequirement}
          traitDetailValue={traitDetailValue}
          onChange={handleSaveTraitDetail}
          immutable={traitDetail?.immutable}
        />
      )

    case 'text': {
      const options = params?.options as DataEntryOption[]
      if (params?.display === 'tile') {
        return (
          <DataEntryTilesSelector
            options={options}
            taskData={taskData}
            detailRequirement={detailRequirement}
            traitConfig={traitStatus}
            traitDetailValue={traitDetailValue}
            handleChangeDetail={handleSaveTraitDetail}
            immutable={traitDetail?.immutable}
            removeText={params?.removeText}
            selectText={params?.selectText}
          />
        )
      } else {
        if (params?.gate) {
          return (
            <DataEntryGateGroup
              traitConfig={traitStatus}
              detailRequirement={detailRequirement}
              traitDetailValue={traitDetailValue}
              onChange={handleSaveTraitDetail}
              immutable={traitDetail?.immutable}
            />
          )
        } else if (params?.multiChoice) {
          return (
            <DataEntryCheckboxList
              options={options}
              taskData={taskData}
              detailRequirement={detailRequirement}
              traitConfig={traitStatus}
              traitDetailValue={traitDetailValue}
              handleChangeDetail={handleSaveTraitDetail}
              immutable={traitDetail?.immutable}
            />
          )
        } else {
          return (
            <DataEntryRadioGroup
              traitConfig={traitStatus}
              detailRequirement={detailRequirement}
              traitDetailValue={traitDetailValue}
              onChange={handleSaveTraitDetail}
              immutable={traitDetail?.immutable}
            />
          )
        }
      }
    }

    case 'date':
      return (
        <DatePickerTraitEditor
          taskId={taskId}
          taskData={taskData}
          traitConfig={traitStatus}
          detailRequirement={detailRequirement}
          resourceId={resourceId}
          traitDetailValue={traitDetailValue}
          onChange={handleSaveTraitDetail}
          immutable={traitDetail?.immutable}
        />
      )

    case 'email':
      return (
        <EmailDataEntryField
          taskId={taskId}
          taskData={taskData}
          traitConfig={traitStatus}
          detailRequirement={detailRequirement}
          resourceId={resourceId}
          traitDetailValue={traitDetailValue}
          onChange={handleSaveTraitDetail}
          immutable={traitDetail?.immutable}
          saveError={saveError}
          ciboSourced={ciboSourced}
        />
      )

    case 'longText':
      return (
        <LongTextDataEntryField
          taskId={taskId}
          taskData={taskData}
          traitConfig={traitStatus}
          detailRequirement={detailRequirement}
          resourceId={resourceId}
          traitDetailValue={traitDetailValue}
          onChange={handleSaveTraitDetail}
          immutable={traitDetail?.immutable}
        />
      )

    case 'number':
      return (
        <NumberDataEntryField
          taskId={taskId}
          taskData={taskData}
          traitConfig={traitStatus}
          detailRequirement={detailRequirement}
          resourceId={resourceId}
          traitDetailValue={traitDetailValue}
          onChange={handleSaveTraitDetail}
          immutable={traitDetail?.immutable}
        />
      )

    case 'yearlyCheckboxes':
      console.error(`yearlyCheckboxes does not support dataType '${resolvedDataType}'`)
      return <></>

    case 'file':
      return (
        <DataEntryFileUpload
          taskId={taskId}
          taskData={taskData}
          traitConfig={traitStatus}
          detailRequirement={detailRequirement}
        />
      )
  }
}
